import React from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { Button, Form, FormGroup } from 'reactstrap';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import intl from 'react-intl-universal';

import Icon from 'components/Icon';
import Radio from 'components/Radio';
import Page from 'components/Page';
import PageHeader from 'components/PageHeader';
import PageFooter from 'components/PageFooter';
import FormBlock from 'modules/forms/components/FormBlock';
import {
  EAppointmentFormStatus,
  EFormDisqualificationType,
  IForm,
} from 'modules/forms/interfaces/form.interfaces';
import { postAppointmentForm, setFormData } from 'modules/forms/actions/form.actions';
import { acuteSinusitisRoutes } from 'modules/forms/constants/routes.constants';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { getFormResultHTML } from 'modules/forms/helpers/formResult.helper';
import FormResult from './FormResult';

const ThirdAssessment = (): JSX.Element => {
  const history = useHistory();
  const dispatch = useDispatch();
  const appointment = useTypedSelector(state => state.form.appointment);
  const stateForm = useTypedSelector(state => state.form.form);
  const methods = useForm<IForm>({
    defaultValues: {
      hasThirdSymptoms: stateForm.hasThirdSymptoms,
    },
    mode: 'onSubmit',
  });
  const { control, watch, handleSubmit } = methods;
  const hasThirdSymptoms = watch('hasThirdSymptoms');

  const onBack = () => {
    history.goBack();
  };

  const onSubmit = (values: IForm) => {
    dispatch(setFormData(values));
    if (values.hasThirdSymptoms === true) {
      dispatch(
        setFormData({
          disqualificationType: EFormDisqualificationType.TO_A_AND_E,
        }),
      );
      dispatch(
        postAppointmentForm({
          form: {
            appointmentUuid: appointment?.uuid,
            status: EAppointmentFormStatus.TO_A_AND_E,
            response: getFormResultHTML({ ...stateForm, ...values }, FormResult),
          },
          route: acuteSinusitisRoutes.disqualification,
        }),
      );
    } else {
      history.push({
        pathname: acuteSinusitisRoutes.fourthAssessment,
        search: history.location.search,
      });
    }
  };

  return (
    <Page>
      <PageHeader step={4} maxSteps={7} />
      <FormProvider {...methods}>
        <Form className="page" onSubmit={handleSubmit(onSubmit)}>
          <div className="page-content">
            <main id="page-main-scrollable" className="page-main">
              <FormBlock
                title="Assessment"
                description="Has the patient experienced sinus problems that cause visible swelling on the front of their head (red bump)?"
              >
                <Controller
                  name="hasThirdSymptoms"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormGroup>
                      <Radio checked={value === true} onChange={() => onChange(true)} label="Yes" />
                      <Radio
                        checked={value === false}
                        onChange={() => onChange(false)}
                        label="No"
                      />
                    </FormGroup>
                  )}
                />
              </FormBlock>
            </main>

            <PageFooter>
              <Button color="primary" outline type="button" block onClick={onBack}>
                <Icon iconName="arrow-left" />
                {intl.get('button.back')}
              </Button>
              <Button
                color="primary"
                type="button"
                block
                disabled={hasThirdSymptoms === undefined}
                onClick={handleSubmit(onSubmit)}
              >
                {intl.get('button.next')} <Icon iconName="arrow-right" />
              </Button>
            </PageFooter>
          </div>
        </Form>
      </FormProvider>
    </Page>
  );
};

export default ThirdAssessment;
